import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import Amount from "../../ui-components/molecules/amount/amount";
import Tag from "../../ui-components/molecules/tag/tag";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import { TagColor } from "../../app/data/common/tagColor";
import { PublicPaymentLinkSummary } from "../../app/data/payment/PaymentSession";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";

const columnHelper = createColumnHelper<PublicPaymentLinkSummary>();

export const LINK_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "dateCreated", label: "Created" },
  { id: "account", label: "Account" },
  { id: "email", label: "Email" },
  { id: "probillNumbers", label: "XGS probill numbers" },
  { id: "status", label: "Status" },
  { id: "expiresAt", label: "Expires" },
  { id: "openCount", label: "Opens" },
  { id: "lastOpenedAt", label: "Last opened" },
  { id: "payment", label: "Payment outcome" },
  { id: "actions", label: "Actions" },
];

export const LINK_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [];

const when = (value?: string) => (value ? moment(value).format("MM/DD/YYYY h:mm A") : "—");

const statusColor = (status: string): TagColor => {
  if (status === "ACTIVE") return TagColor.GREEN;
  if (status === "PAID") return TagColor.BLUE;
  if (status === "REVOKED") return TagColor.RED;
  return TagColor.GREY;
};

const statusLabel = (status: string) =>
  status ? status.charAt(0) + status.slice(1).toLowerCase() : "—";

const probills = (row: PublicPaymentLinkSummary) => {
  const numbers = row.probillNumbers || [];
  if (!numbers.length) return "—";
  if (numbers.length === 1) return formatProbillDisplay(numbers[0]);
  return (
    <span>
      {formatProbillDisplay(numbers[0])}
      <span className="xgs-public-payment-links__extra">+{numbers.length - 1}</span>
    </span>
  );
};

export const getPublicPaymentLinkColumns = (
  onCopy: (link: PublicPaymentLinkSummary) => void,
  onRevoke: (link: PublicPaymentLinkSummary) => void
) => [
  columnHelper.accessor("dateCreated", {
    id: "dateCreated",
    header: "Created",
    cell: (info) => (
      <span>
        <span>{when(info.getValue())}</span>
        {info.row.original.createdBy && (
          <span className="xgs-public-payment-links__sub">{info.row.original.createdBy}</span>
        )}
      </span>
    ),
    size: 190,
  }),
  columnHelper.display({
    id: "account",
    header: "Account",
    cell: (info) => {
      const link = info.row.original;
      if (!link.accountNumber && !link.accountName) return "—";
      return (
        <span>
          <span>{link.accountName || "—"}</span>
          {link.accountNumber != null && (
            <span className="xgs-public-payment-links__sub">{link.accountNumber}</span>
          )}
        </span>
      );
    },
    size: 180,
  }),
  columnHelper.accessor("email", {
    id: "email",
    header: "Email",
    cell: (info) => info.getValue() || "—",
    size: 220,
  }),
  columnHelper.accessor("probillNumbers", {
    id: "probillNumbers",
    header: "XGS probill numbers",
    cell: (info) => probills(info.row.original),
    size: 180,
  }),
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    cell: (info) => (
      <Tag mods={{ color: statusColor(info.getValue()) }}>{statusLabel(info.getValue())}</Tag>
    ),
    size: 120,
  }),
  columnHelper.accessor("expiresAt", {
    id: "expiresAt",
    header: "Expires",
    cell: (info) => when(info.getValue()),
    size: 170,
  }),
  columnHelper.accessor("openCount", {
    id: "openCount",
    header: "Opens",
    cell: (info) => info.getValue() ?? 0,
    size: 90,
  }),
  columnHelper.accessor("lastOpenedAt", {
    id: "lastOpenedAt",
    header: "Last opened",
    cell: (info) => when(info.getValue()),
    size: 170,
  }),
  columnHelper.display({
    id: "payment",
    header: "Payment outcome",
    cell: (info) => {
      const link = info.row.original;
      if (link.status !== "PAID" && link.paymentAmountCents == null) return "—";
      return (
        <span>
          {link.paymentAmountCents != null ? (
            <Amount amount={link.paymentAmountCents / 100} />
          ) : (
            "Paid"
          )}
          {link.paymentMethod && (
            <span className="xgs-public-payment-links__sub">{link.paymentMethod}</span>
          )}
        </span>
      );
    },
    size: 140,
  }),
  columnHelper.display({
    id: "actions",
    header: "",
    enableSorting: false,
    size: 88,
    cell: (info) => {
      const link = info.row.original;
      return (
        <span className="xgs-public-payment-links__actions">
          <button
            type="button"
            className="xgs-public-payment-links__icon"
            aria-label="Copy link"
            title="Copy link"
            onClick={(event) => {
              event.stopPropagation();
              onCopy(link);
            }}
          >
            <XGSIcon icon={XGSIcons.faCopy} />
          </button>
          {link.status === "ACTIVE" && (
            <button
              type="button"
              className="xgs-public-payment-links__icon xgs-public-payment-links__icon--danger"
              aria-label="Revoke link"
              title="Revoke link"
              onClick={(event) => {
                event.stopPropagation();
                onRevoke(link);
              }}
            >
              <XGSIcon icon={XGSIcons.faBan} />
            </button>
          )}
        </span>
      );
    },
  }),
];
